import { Address, Amount, ProtocolKeyService } from "@silo/types";
import { Effect, Option, pipe, Ref } from "effect";
import {
  IncentivizedShareToken,
  IncentivizedShareTokensService,
} from "./types";

export type TrackIncentivizedShareTokens = ReturnType<
  typeof trackIncentivizedShareTokens
>;
export type TrackIncentivizedShareTokensSuccess =
  Effect.Effect.Success<TrackIncentivizedShareTokens>;
export type TrackIncentivizedShareTokensError =
  Effect.Effect.Error<TrackIncentivizedShareTokens>;
export type TrackIncentivizedShareTokensContext =
  Effect.Effect.Context<TrackIncentivizedShareTokens>;

interface TrackIncentivizedShareTokensParams {
  collateralShareToken: Address;
  collateralEmissionPerSecond: Amount;
  protectedShareToken: Address;
  protectedEmissionPerSecond: Amount;
  debtShareToken: Address;
  debtEmissionPerSecond: Amount;
}

export function trackIncentivizedShareTokens({
  collateralShareToken,
  collateralEmissionPerSecond,
  protectedShareToken,
  protectedEmissionPerSecond,
  debtShareToken,
  debtEmissionPerSecond,
}: TrackIncentivizedShareTokensParams) {
  return Effect.gen(function* () {
    const option = yield* Effect.serviceOption(IncentivizedShareTokensService);

    if (Option.isNone(option)) {
      return;
    }

    const protocolKey = yield* ProtocolKeyService;
    const incentivizedShareTokens = option.value;

    if (collateralEmissionPerSecond > 0n) {
      yield* pipe(
        incentivizedShareTokens,
        Ref.update((arr) => {
          const token: IncentivizedShareToken = {
            protocolKey,
            type: "collateral",
            shareTokenAddress: collateralShareToken,
          };

          return [...arr, token];
        })
      );
    }

    if (protectedEmissionPerSecond > 0n) {
      yield* pipe(
        incentivizedShareTokens,
        Ref.update((arr) => {
          const token: IncentivizedShareToken = {
            protocolKey,
            type: "protected",
            shareTokenAddress: protectedShareToken,
          };

          return [...arr, token];
        })
      );
    }

    if (debtEmissionPerSecond > 0n) {
      yield* pipe(
        incentivizedShareTokens,
        Ref.update((arr) => {
          const token: IncentivizedShareToken = {
            protocolKey,
            type: "debt",
            shareTokenAddress: debtShareToken,
          };

          return [...arr, token];
        })
      );
    }
  });
}
